import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

interface AddDayDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    date: string;
    mark_entry?: string;
    lunch_entry?: string;
    lunch_exit?: string;
    final_exit?: string;
  }) => Promise<void>;
  workerId: number;
}

export const AddDayDialog: React.FC<AddDayDialogProps> = ({ open, onClose, onSave, workerId }) => {
  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const [markEntry, setMarkEntry] = useState<Dayjs | null>(null);
  const [lunchEntry, setLunchEntry] = useState<Dayjs | null>(null);
  const [lunchExit, setLunchExit] = useState<Dayjs | null>(null);
  const [finalExit, setFinalExit] = useState<Dayjs | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    try {
      setError(null);

      if (!date) {
        setError('La fecha es requerida');
        return;
      }

      const data = {
        date: date.format('YYYY-MM-DD'),
        mark_entry: markEntry ? markEntry.format('HH:mm') : undefined,
        lunch_entry: lunchEntry ? lunchEntry.format('HH:mm') : undefined,
        lunch_exit: lunchExit ? lunchExit.format('HH:mm') : undefined,
        final_exit: finalExit ? finalExit.format('HH:mm') : undefined,
      };

      setSaving(true);
      await onSave(data);
      
      // Resetear formulario
      setDate(dayjs());
      setMarkEntry(null);
      setLunchEntry(null);
      setLunchExit(null);
      setFinalExit(null);
      setError(null);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al guardar el registro');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setDate(dayjs());
    setMarkEntry(null);
    setLunchEntry(null);
    setLunchExit(null);
    setFinalExit(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Agregar Día Manual</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Fecha *"
              value={date}
              onChange={(newValue) => setDate(newValue)}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                },
              }}
            />

            <TimePicker
              label="Hora de Entrada"
              value={markEntry}
              onChange={(newValue) => setMarkEntry(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />

            <TimePicker
              label="Inicio de Almuerzo"
              value={lunchEntry}
              onChange={(newValue) => setLunchEntry(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />

            <TimePicker
              label="Fin de Almuerzo"
              value={lunchExit}
              onChange={(newValue) => setLunchExit(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />

            <TimePicker
              label="Hora de Salida"
              value={finalExit}
              onChange={(newValue) => setFinalExit(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>

          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            Nota: Si proporcionas la hora de salida, el sistema calculará automáticamente las horas trabajadas y el pago.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancelar
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
